(() => {
  const initQuiz = (root) => {
    if (!root || root.dataset.quizReady === 'true') return;
    root.dataset.quizReady = 'true';

    const panels = [...root.querySelectorAll('[data-quiz-panel]')];
    const progress = root.querySelector('[data-quiz-progress]');
    const topbar = root.querySelector('[data-quiz-topbar]');
    const progressLabel = root.querySelector('[data-quiz-progress-label]');
    const progressBar = root.querySelector('[data-quiz-progress-bar]');
    const navigation = root.querySelector('[data-quiz-navigation]');
    const answers = {};
    const history = [];
    let current = 'intro';

    const productMeta = {};
    root.querySelectorAll('[data-quiz-product]').forEach((card) => {
      productMeta[card.dataset.quizProduct] = {
        card,
        title: card.querySelector('h3')?.textContent.trim() || card.dataset.quizProduct,
        url: card.dataset.productUrl || '#',
      };
    });

    const nextPanel = (panel) => {
      if (panel === 'use_case') return 'head_size';
      if (panel === 'head_size') return answers.use_case === 'hijab' ? 'hijab_priority' : 'hair_volume';
      if (panel === 'hair_volume') return 'lace';
      if (panel === 'lace') return 'wig_length';
      if (panel === 'wig_length') return 'contact';
      if (panel === 'hijab_priority') return 'hijab_fabric';
      if (panel === 'hijab_fabric') return 'hijab_color';
      if (panel === 'hijab_color') return 'contact';
      if (panel === 'contact') return 'result';
      return 'use_case';
    };

    const updateChrome = (panelName) => {
      const panel = panels.find((item) => item.dataset.quizPanel === panelName);
      const step = Number(panel?.dataset.progress || 0);
      const showProgress = step > 0 && panelName !== 'result';
      topbar.hidden = !showProgress;
      progress.hidden = !showProgress;
      navigation.hidden = panelName === 'intro' || panelName === 'result';
      if (showProgress) {
        progressLabel.textContent = `Step ${step} of 6`;
        progressBar.style.width = `${(step / 6) * 100}%`;
      }
    };

    const showPanel = (panelName, pushHistory = true) => {
      if (pushHistory && current !== panelName) history.push(current);
      current = panelName;
      panels.forEach((panel) => {
        const active = panel.dataset.quizPanel === panelName;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      updateChrome(panelName);
      const heading = root.querySelector(`[data-quiz-panel="${panelName}"] h1, [data-quiz-panel="${panelName}"] h2`);
      window.requestAnimationFrame(() => heading?.focus({ preventScroll: true }));
      root.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    };

    const colorGroup = () => {
      if (answers.use_case === 'hijab') return answers.hijab_color || 'all';
      return answers.lace === 'yes' ? 'dark' : 'all';
    };

    const colorFits = (name, group) => {
      const value = name.toLowerCase();
      if (group === 'dark') return value.includes('black') || value.includes('dark brown');
      if (group === 'light') return value.includes('beige') || value.includes('tan') || (value.includes('brown') && !value.includes('dark'));
      return true;
    };

    const calculateResult = () => {
      let products = ['wide'];
      const reasons = [];
      let note = '';

      if (answers.use_case === 'hijab') {
        products = answers.hijab_priority === 'gentle' ? ['cap', 'wide'] : ['wide', 'cap'];
        reasons.push(answers.hijab_priority === 'gentle'
          ? 'For a gentle, comfortable foundation, both the Wide Wig Grip Band and 2-in-1 Wig Grip Cap are well suited to your routine.'
          : 'For secure all-day hold, both the Wide Wig Grip Band and 2-in-1 Wig Grip Cap provide a stable foundation.');
        if (answers.head_size === 'over22') {
          reasons.push('For a head circumference over 22 inches, the Wide Wig Grip Band and 2-in-1 Wig Grip Cap provide a roomier, more comfortable fit.');
        }
        if (['silk', 'satin', 'chiffon'].includes(answers.hijab_fabric)) {
          note = 'For silk, satin or chiffon, pairing your grip with a cotton or bamboo underlayer can provide additional stability.';
        }
      } else {
        const length = answers.wig_length;
        const volume = answers.hair_volume;
        const large = answers.head_size === 'over22';

        if (volume === 'full') {
          products = length === 'short' && !large ? ['regular', 'wide'] : ['wide'];
        } else {
          products = ['cap', 'wide'];
        }

        if (length === 'long') {
          reasons.push('For longer styles, gather your natural hair into a low bun or use a wig cap before placing the Wide Wig Grip Band for a smooth, secure foundation.');
        } else if (length === 'medium') {
          reasons.push('Medium-length styles benefit from balanced support that helps keep the wig secure while maintaining a natural, comfortable fit.');
        } else {
          reasons.push('Shorter styles work well with a smooth, low-profile foundation that helps the wig sit naturally around the hairline and nape.');
        }
        reasons.push(volume === 'full'
          ? 'With fuller natural hair, a band-only style gives you more flexibility to arrange and contain your hair underneath.'
          : `With ${volume === 'medium' ? 'medium' : 'light'} hair volume, both the Wide Wig Grip Band and 2-in-1 Wig Grip Cap can provide comfortable support.`);
        if (large) reasons.push('With a head circumference over 22 inches, the Wide collection offers a more comfortable, better-proportioned fit.');
        note = answers.lace === 'yes'
          ? 'For lace at the front or top, darker shades can blend more naturally with roots, lace and knots.'
          : 'For the most seamless blend, choose a shade close to your natural skin tone.';
      }

      return { products: [...new Set(products)], reasons, note, group: colorGroup() };
    };

    const prepareProduct = (key, group) => {
      const card = productMeta[key]?.card;
      if (!card) return;

      const swatches = [...card.querySelectorAll('[data-variant-swatches] [data-color]')];
      let visibleCount = 0;
      swatches.forEach((swatch) => {
        const show = colorFits(swatch.dataset.color || '', group);
        swatch.hidden = !show;
        swatch.classList.remove('is-selected');
        swatch.setAttribute('aria-pressed', 'false');
        if (show) visibleCount += 1;
      });
      if (!visibleCount) swatches.forEach((swatch) => { swatch.hidden = false; });

      const input = card.querySelector('[data-quiz-variant-input]');
      const add = card.querySelector('[data-quiz-add]');
      const selected = card.querySelector('[data-selected-shade]');
      if (input) input.value = '';
      if (add) add.disabled = true;
      if (selected) selected.textContent = 'Choose a shade';
    };

    const prepareProducts = (keys, group) => {
      const visibleKeys = keys.filter((key) => productMeta[key]);
      root.querySelectorAll('[data-quiz-product]').forEach((card) => {
        card.hidden = !visibleKeys.includes(card.dataset.quizProduct);
        card.style.order = `${Math.max(visibleKeys.indexOf(card.dataset.quizProduct), 0)}`;
      });
      visibleKeys.forEach((key) => prepareProduct(key, group));
      const productResults = root.querySelector('.dream-quiz__product-results');
      productResults?.classList.toggle('is-multiple', visibleKeys.length > 1);
      return visibleKeys;
    };

    const renderResult = () => {
      const result = calculateResult();
      const reasons = root.querySelector('[data-quiz-reasons]');
      const note = root.querySelector('[data-quiz-note]');
      reasons.innerHTML = result.reasons.map((reason) => `<li>${reason}</li>`).join('');
      note.textContent = result.note;
      note.hidden = !result.note;
      const visibleProducts = prepareProducts(result.products, result.group);
      const intro = root.querySelector('[data-quiz-result-intro]');
      if (intro) {
        intro.textContent = visibleProducts.length > 1
          ? 'Based on your answers, these recommendations are well suited to your routine.'
          : 'Based on your answers, this recommendation is well suited to your routine.';
      }

      const alternative = root.querySelector('[data-quiz-alternative]');
      alternative.hidden = true;

      return result;
    };

    const contactSummary = () => {
      const result = calculateResult();
      return [
        'Dreamlover Wig Grip Fit Finder',
        ...Object.entries(answers).map(([key, value]) => `${key}: ${value}`),
        `recommended_products: ${result.products.map((key) => productMeta[key]?.title || key).join(', ')}`,
        `recommended_color_group: ${result.group}`,
      ].join('\n');
    };

    root.querySelector('[data-quiz-start]')?.addEventListener('click', () => showPanel('use_case'));

    root.querySelectorAll('[data-answer-key]').forEach((button) => {
      button.addEventListener('click', () => {
        const key = button.dataset.answerKey;
        answers[key] = button.dataset.answerValue;
        root.querySelectorAll(`[data-answer-key="${key}"]`).forEach((item) => item.classList.toggle('is-selected', item === button));
        window.setTimeout(() => showPanel(nextPanel(current)), 150);
      });
    });

    root.querySelector('[data-quiz-back]')?.addEventListener('click', () => {
      const previous = history.pop();
      if (previous) showPanel(previous, false);
    });

    root.querySelector('[data-quiz-skip]')?.addEventListener('click', () => {
      renderResult();
      showPanel('result');
    });

    const contactForm = root.querySelector('.dream-quiz__contact-form');
    contactForm?.addEventListener('submit', () => {
      const summary = contactForm.querySelector('[data-quiz-contact-summary]');
      if (summary) summary.value = contactSummary();
      renderResult();
      window.setTimeout(() => showPanel('result'), 120);
    });

    root.querySelectorAll('[data-variant-swatches]').forEach((group) => {
      group.addEventListener('click', (event) => {
        const swatch = event.target.closest('[data-variant-id]');
        if (!swatch || swatch.disabled) return;
        const card = swatch.closest('[data-quiz-product]');
        card.querySelectorAll('[data-variant-id]').forEach((item) => {
          item.classList.toggle('is-selected', item === swatch);
          item.setAttribute('aria-pressed', item === swatch ? 'true' : 'false');
        });
        card.querySelector('[data-quiz-variant-input]').value = swatch.dataset.variantId;
        card.querySelector('[data-quiz-add]').disabled = false;
        card.querySelector('[data-selected-shade]').textContent = swatch.dataset.color;
      });
    });

    root.querySelector('[data-quiz-retake]')?.addEventListener('click', () => {
      Object.keys(answers).forEach((key) => delete answers[key]);
      history.length = 0;
      root.querySelectorAll('.is-selected').forEach((item) => item.classList.remove('is-selected'));
      showPanel('intro', false);
    });

    updateChrome('intro');
  };

  const initAll = () => document.querySelectorAll('[data-dream-quiz]').forEach(initQuiz);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initQuiz(event.target.querySelector('[data-dream-quiz]')));
})();
